import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MIN_CALIBRATION_SAMPLES, MIN_COMFORT_SAMPLES, comfortDirectionDeltas, comfortRangesFromSamples, headInputFromPose, stableCalibration, type ComfortRanges, type ComfortSamples, type HeadInput, type HeadPose } from "../shared/tracking.ts";
import { useHeadTracker, type TrackerStatus } from "./useHeadTracker.ts";

export type ComfortDirection = keyof ComfortRanges;
export type HeadInputStatus = TrackerStatus | "calibrating" | "comfort-calibrating" | "ready";

const CALIBRATION_DURATION_MS = 2_500;
const MAX_CALIBRATION_SAMPLES = 40;
const COMFORT_STEPS: Array<{ direction: ComfortDirection; returning: boolean; durationMs: number }> = [
  { direction: "left", returning: false, durationMs: 2_200 },
  { direction: "left", returning: true, durationMs: 900 },
  { direction: "right", returning: false, durationMs: 2_200 },
  { direction: "right", returning: true, durationMs: 900 },
  { direction: "up", returning: false, durationMs: 2_200 },
  { direction: "up", returning: true, durationMs: 900 },
  { direction: "down", returning: false, durationMs: 2_200 },
  { direction: "down", returning: true, durationMs: 900 },
];

function emptyComfortSamples(): ComfortSamples {
  return { left: [], right: [], up: [], down: [] };
}

/**
 * Reusable game-facing head input. Games consume only this normalized contract;
 * detector keypoints, pose filtering and neutral calibration stay behind it.
 */
export function useHeadInput({ calibrateComfort = false }: { calibrateComfort?: boolean } = {}) {
  const tracker = useHeadTracker();
  const [baseline, setBaseline] = useState<HeadPose | null>(null);
  const [input, setInput] = useState<HeadInput | null>(null);
  const [calibrationProgress, setCalibrationProgress] = useState(0);
  const [comfortRanges, setComfortRanges] = useState<ComfortRanges | null>(null);
  const [comfortStepIndex, setComfortStepIndex] = useState(0);
  const [comfortRetry, setComfortRetry] = useState(false);
  const samplesRef = useRef<HeadPose[]>([]);
  const comfortSamplesRef = useRef<ComfortSamples>(emptyComfortSamples());
  const calibrationStartedRef = useRef<number | null>(null);
  const comfortStepStartedRef = useRef<number | null>(null);
  const previousInputRef = useRef<HeadInput | null>(null);

  const resetInput = useCallback(() => {
    setBaseline(null);
    setInput(null);
    setCalibrationProgress(0);
    setComfortRanges(null);
    setComfortStepIndex(0);
    setComfortRetry(false);
    samplesRef.current = [];
    comfortSamplesRef.current = emptyComfortSamples();
    calibrationStartedRef.current = null;
    comfortStepStartedRef.current = null;
    previousInputRef.current = null;
  }, []);

  const stop = useCallback(() => {
    tracker.stop();
    resetInput();
  }, [resetInput, tracker.stop]);

  const start = useCallback(async () => {
    resetInput();
    await tracker.start();
  }, [resetInput, tracker.start]);

  useEffect(() => {
    if (tracker.status !== "tracking" || !tracker.pose) return;

    if (!baseline) {
      const startedAt = calibrationStartedRef.current ?? tracker.pose.timestamp;
      calibrationStartedRef.current = startedAt;
      samplesRef.current = [...samplesRef.current.slice(-(MAX_CALIBRATION_SAMPLES - 1)), tracker.pose];
      const progress = Math.min(1,
        (tracker.pose.timestamp - startedAt) / CALIBRATION_DURATION_MS,
        samplesRef.current.length / MIN_CALIBRATION_SAMPLES,
      );
      setCalibrationProgress(progress);
      if (progress < 1) return;

      const stable = stableCalibration(samplesRef.current);
      if (stable) {
        setBaseline(stable);
        setCalibrationProgress(1);
      } else {
        samplesRef.current = [];
        calibrationStartedRef.current = tracker.pose.timestamp;
        setCalibrationProgress(0);
      }
      return;
    }

    if (calibrateComfort && !comfortRanges) {
      const step = COMFORT_STEPS[comfortStepIndex];
      const startedAt = comfortStepStartedRef.current ?? tracker.pose.timestamp;
      comfortStepStartedRef.current = startedAt;
      const elapsed = tracker.pose.timestamp - startedAt;
      if (!step.returning && elapsed >= 250) {
        const samples = comfortSamplesRef.current[step.direction];
        comfortSamplesRef.current[step.direction] = [...samples.slice(-39), tracker.pose];
      }
      setCalibrationProgress((comfortStepIndex + Math.min(1, elapsed / step.durationMs)) / COMFORT_STEPS.length);
      if (elapsed < step.durationMs) return;
      if (!step.returning && comfortDirectionDeltas(baseline, step.direction, comfortSamplesRef.current[step.direction]).length < MIN_COMFORT_SAMPLES) return;
      comfortStepStartedRef.current = null;
      if (comfortStepIndex < COMFORT_STEPS.length - 1) {
        setComfortStepIndex((value) => value + 1);
        return;
      }
      const ranges = comfortRangesFromSamples(baseline, comfortSamplesRef.current);
      if (ranges) {
        setComfortRanges(ranges);
        setComfortRetry(false);
        setCalibrationProgress(1);
      } else {
        comfortSamplesRef.current = emptyComfortSamples();
        setComfortStepIndex(0);
        setComfortRetry(true);
        setCalibrationProgress(0);
      }
      return;
    }

    const next = headInputFromPose(tracker.pose, baseline, previousInputRef.current, comfortRanges || undefined);
    previousInputRef.current = next;
    setInput(next);
  }, [baseline, calibrateComfort, comfortRanges, comfortStepIndex, tracker.pose, tracker.status]);

  useEffect(() => {
    if (baseline || !["face-lost", "waiting-frame"].includes(tracker.status)) return;
    samplesRef.current = [];
    calibrationStartedRef.current = null;
    setCalibrationProgress(0);
  }, [baseline, tracker.status]);

  const status: HeadInputStatus = tracker.status === "tracking"
    ? !baseline ? "calibrating" : calibrateComfort && !comfortRanges ? "comfort-calibrating" : "ready"
    : tracker.status;
  const comfortStep = COMFORT_STEPS.at(comfortStepIndex);

  const gameInput = useMemo<HeadInput | null>(() => {
    if (status === "ready") return input;
    if (!["recovering", "face-lost"].includes(status) || !input) return null;
    return {
      ...input,
      isTracked: false,
      trackingQuality: status === "face-lost" ? "lost" : "fair",
      isTooFast: false,
    };
  }, [input, status]);

  return {
    videoRef: tracker.videoRef,
    status,
    input: gameInput,
    error: tracker.error,
    loadProgress: tracker.loadProgress,
    calibrationProgress,
    comfortDirection: comfortStep?.direction || null,
    comfortReturning: Boolean(comfortStep?.returning),
    comfortRetry,
    start,
    stop,
  };
}
